/**
 * "Base Image:" / "Overlay:" — `GUI_launcher.py`'s two-row form, extended so
 * overlay is a checklist rather than one combo box: several files can be on
 * screen at once, each in the color shown next to its name.
 */

import { Check } from "lucide-react";

import { cn } from "@/lib/utils";
import { OVERLAY_PALETTE } from "./SliceCanvas";

export interface PickerFile {
  id: string;
  label: string;
}

export function overlayColor(index: number): [number, number, number] {
  return OVERLAY_PALETTE[index % OVERLAY_PALETTE.length];
}

function rgb(c: [number, number, number]): string {
  return `rgb(${c[0]}, ${c[1]}, ${c[2]})`;
}

export function BaseOverlayPicker({
  files,
  baseId,
  overlayIds,
  onBaseChange,
  onOverlayToggle,
}: {
  files: PickerFile[];
  baseId: string | null;
  overlayIds: string[];
  onBaseChange: (id: string) => void;
  onOverlayToggle: (id: string) => void;
}) {
  return (
    <div className="flex flex-col gap-2 border-b border-ads-border bg-ads-surface px-3 py-2">
      <label className="flex items-center gap-2 text-[11.5px]">
        <span className="w-16 shrink-0 text-neutral-500">Base Image</span>
        <select
          className="h-7 min-w-0 flex-1 rounded border border-ads-border bg-white px-2 text-[11.5px]"
          value={baseId ?? ""}
          onChange={(e) => onBaseChange(e.target.value)}
        >
          <option value="" disabled>
            Choose a file…
          </option>
          {files.map((f) => (
            <option key={f.id} value={f.id}>
              {f.label}
            </option>
          ))}
        </select>
      </label>

      <div className="flex items-start gap-2 text-[11.5px]">
        <span className="w-16 shrink-0 pt-1 text-neutral-500">Overlay</span>
        <div className="flex min-w-0 flex-1 flex-wrap gap-1">
          {files.length === 0 ? (
            <span className="text-neutral-400">No other files here.</span>
          ) : (
            files
              .filter((f) => f.id !== baseId)
              .map((f) => {
                const idx = overlayIds.indexOf(f.id);
                const checked = idx !== -1;
                return (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => onOverlayToggle(f.id)}
                    className={cn(
                      "flex items-center gap-1 rounded border px-1.5 py-0.5 text-[11px] transition",
                      checked
                        ? "border-ads-accent-border bg-ads-accent-soft text-ads-text"
                        : "border-ads-border text-neutral-600 hover:bg-ads-hover",
                    )}
                    title={f.label}
                  >
                    <span
                      className="h-2.5 w-2.5 shrink-0 rounded-full border border-black/10"
                      style={{ background: checked ? rgb(overlayColor(idx)) : "#d4d4d4" }}
                    />
                    {checked ? <Check className="h-2.5 w-2.5 shrink-0" /> : null}
                    <span className="max-w-[9rem] truncate">{f.label}</span>
                  </button>
                );
              })
          )}
        </div>
      </div>
    </div>
  );
}
